import { useState, useRef } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import {
  getWatchBrands,
  getReferencesForBrandPaginated,
  updateWatchReference,
} from '../../services/firebase';
import type { PaginatedReferencesResult } from '../../services/firebase';
import type { WatchReference } from '../../types';
import toast from 'react-hot-toast';

/** Called after a run completes so the parent can refresh its reference list. */
interface BackfillProductUrlsProps {
  onComplete?: () => void;
}

type RowStatus = 'pending' | 'processing' | 'updated' | 'failed';

interface BackfillRow {
  brandId: string;
  brandName: string;
  referenceId: string;
  referenceLabel: string;
  productUrl: string;
  status: RowStatus;
  error?: string;
}

/**
 * One-time admin migration: copies the legacy "Product Page" entry from a
 * reference's `links[]` array into the dedicated `productUrl` field, for any
 * reference across all brands that is missing `productUrl`. New imports already
 * set `productUrl` directly (see BulkImport), so this only needs running once
 * to bring existing references up to date. The `links[]` entry is left intact.
 */
export function BackfillProductUrls({ onComplete }: BackfillProductUrlsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(0);
  const [rows, setRows] = useState<BackfillRow[]>([]);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const abortRef = useRef(false);

  const updatedCount = rows.filter((r) => r.status === 'updated').length;
  const failedCount = rows.filter((r) => r.status === 'failed').length;

  /**
   * The labeled product-page link URL on a ref, if present. Matches the exact
   * shape the importer writes: title "Product Page" (case-insensitive) AND
   * type "official". `source.url` is intentionally NOT consulted — it holds an
   * API URL on older scraped refs.
   */
  const productPageLinkUrl = (ref: WatchReference): string | undefined =>
    ref.links?.find(
      (l) => l.title?.trim().toLowerCase() === 'product page' && l.type === 'official',
    )?.url;

  const scan = async () => {
    setScanning(true);
    setRows([]);
    setScanned(0);
    setDone(false);
    setProgress(0);
    try {
      const brands = await getWatchBrands();
      const candidates: BackfillRow[] = [];
      let totalScanned = 0;

      for (const brand of brands) {
        const brandName = brand.displayName || brand.name || brand.id;
        let lastDoc: PaginatedReferencesResult['lastDoc'] = null;
        let hasMore = true;
        while (hasMore) {
          const page = await getReferencesForBrandPaginated(brand.id, 200, lastDoc);
          for (const ref of page.references) {
            totalScanned += 1;
            // Skip refs that already have a productUrl — purely additive backfill.
            if (ref.productUrl && ref.productUrl.trim()) continue;
            const url = productPageLinkUrl(ref);
            if (!url) continue;
            candidates.push({
              brandId: brand.id,
              brandName,
              referenceId: ref.id,
              referenceLabel: ref.reference || ref.model || ref.id,
              productUrl: url,
              status: 'pending',
            });
          }
          setScanned(totalScanned);
          lastDoc = page.lastDoc;
          hasMore = page.hasMore && !!page.lastDoc;
        }
      }

      setRows(candidates);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to scan references');
    } finally {
      setScanning(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    void scan();
  };

  const handleClose = () => {
    if (running) return;
    setIsOpen(false);
    setRows([]);
    setScanned(0);
    setDone(false);
    setProgress(0);
    abortRef.current = false;
  };

  const updateRow = (index: number, update: Partial<BackfillRow>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...update } : r)));
  };

  const run = async () => {
    setRunning(true);
    setDone(false);
    setProgress(0);
    abortRef.current = false;

    // Bounded-parallel batches, mirroring RetryMissingImages.
    const BATCH_SIZE = 4;
    const current = rows;
    for (let start = 0; start < current.length; start += BATCH_SIZE) {
      if (abortRef.current) break;
      const batch = current.slice(start, start + BATCH_SIZE);
      await Promise.all(
        batch.map(async (row, offset) => {
          const i = start + offset;
          if (abortRef.current) return;
          updateRow(i, { status: 'processing' });
          try {
            await updateWatchReference(row.brandId, row.referenceId, {
              productUrl: row.productUrl,
            });
            updateRow(i, { status: 'updated' });
          } catch (err) {
            updateRow(i, {
              status: 'failed',
              error: err instanceof Error ? err.message : 'Update failed',
            });
          } finally {
            setProgress((p) => p + 1);
          }
        }),
      );
    }

    setRunning(false);
    setDone(true);
    onComplete?.();
  };

  const total = rows.length;

  return (
    <>
      <Button variant="secondary" onClick={handleOpen}>
        Backfill Product URLs
      </Button>

      <Modal isOpen={isOpen} onClose={handleClose} title="Backfill Product URLs" size="lg">
        <div className="space-y-4">
          {scanning && (
            <p className="text-sm text-gray-600">
              Scanning all brands for references missing a product URL… ({scanned} scanned)
            </p>
          )}

          {!scanning && total === 0 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Nothing to backfill — scanned {scanned} reference{scanned === 1 ? '' : 's'} across all
                brands and found none missing <code>productUrl</code> with a “Product Page” / official
                link in <code>links[]</code>.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => void scan()}>Re-scan</Button>
                <Button onClick={handleClose}>Close</Button>
              </div>
            </div>
          )}

          {!scanning && total > 0 && (
            <>
              <p className="text-sm text-gray-700">
                <span className="font-semibold">{total}</span> reference{total === 1 ? '' : 's'} to backfill
                {' '}— missing <code>productUrl</code> but carrying a “Product Page” / official link.
                {' '}(of {scanned} scanned; {scanned - total} skipped — already set or no labeled link).
                {(running || done) && (
                  <span className="ml-2 text-gray-500">
                    {progress}/{total} processed
                    {(updatedCount > 0 || failedCount > 0) && (
                      <> · <span className="text-green-600">{updatedCount} updated</span> · <span className="text-red-600">{failedCount} failed</span></>
                    )}
                  </span>
                )}
              </p>

              {(running || done) && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full transition-all"
                    style={{ width: `${total ? (progress / total) * 100 : 0}%` }}
                  />
                </div>
              )}

              <div className="max-h-72 overflow-y-auto border rounded-lg divide-y">
                {rows.map((row) => (
                  <div key={`${row.brandId}/${row.referenceId}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <div className="font-mono text-gray-800 truncate">
                        {row.brandName} · {row.referenceLabel}
                      </div>
                      <div className="text-gray-500 truncate" title={row.productUrl}>
                        {row.productUrl}
                      </div>
                    </div>
                    <div className="shrink-0">
                      {row.status === 'pending' && <span className="text-gray-400 text-xs">Queued</span>}
                      {row.status === 'processing' && <span className="text-indigo-600 text-xs">Saving…</span>}
                      {row.status === 'updated' && <span className="text-green-600 text-xs font-medium">✓ Updated</span>}
                      {row.status === 'failed' && (
                        <span className="text-red-600 text-xs" title={row.error}>✗ Failed</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-2">
                {!running && !done && (
                  <>
                    <Button variant="secondary" onClick={handleClose}>Cancel</Button>
                    <Button onClick={() => void run()}>Backfill {total} Reference{total === 1 ? '' : 's'}</Button>
                  </>
                )}
                {running && (
                  <Button variant="secondary" onClick={() => { abortRef.current = true; }}>
                    Stop
                  </Button>
                )}
                {done && (
                  <>
                    <Button variant="secondary" onClick={() => void scan()}>Re-scan</Button>
                    <Button onClick={handleClose}>Done</Button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}
